import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api, { extractError } from "@/lib/api";
import { useState } from "react";
import { Trash, ShoppingCartSimple, ArrowLeft, Percent, Ticket, X, CheckCircle } from "@phosphor-icons/react";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Cart() {
  const { items, pricing, coupon, remove, clear, applyCoupon, refresh } = useCart();
  const { user, refresh: refreshUser } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [couponInput, setCouponInput] = useState("");

  const submitCoupon = (e) => {
    e.preventDefault();
    if (!couponInput.trim()) return;
    applyCoupon(couponInput);
    setCouponInput("");
  };

  const removeCoupon = () => applyCoupon("");

  const checkout = async () => {
    if (!user) { navigate("/login"); return; }
    if (items.length === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/checkout/create-order", { items, coupon_code: coupon || null });
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Failed to load Razorpay");
      const rzp = new window.Razorpay({
        key: data.key_id, amount: data.amount, currency: data.currency, order_id: data.order_id,
        name: "StockTools Pro",
        description: `${items.length} tool subscription(s)`,
        prefill: { email: user.email, name: user.name },
        theme: { color: "#00d0ff" },
        handler: async (resp) => {
          try {
            await api.post("/checkout/verify", resp);
            toast.success("Payment successful! Access unlocked.");
            clear();
            await refreshUser();
            await refresh();
            navigate("/library");
          } catch (err) { toast.error(extractError(err)); }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.open();
    } catch (err) { toast.error(extractError(err)); } finally { setBusy(false); }
  };

  if (items.length === 0) {
    return (
      <div data-testid="cart-page" className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <ShoppingCartSimple size={64} weight="duotone" className="text-muted-foreground mx-auto mb-4" />
          <h1 className="font-display font-black text-3xl mb-2">Your cart is empty</h1>
          <p className="text-muted-foreground mb-6">Add tools from the store to see them here.</p>
          <Link to="/store"><Button data-testid="cart-empty-store-btn" className="bg-primary text-black hover:bg-primary/90 font-bold rounded-sm">Browse tools</Button></Link>
        </div>
      </div>
    );
  }

  const couponInvalid = coupon && pricing && pricing.coupon_code !== coupon;

  return (
    <div data-testid="cart-page" className="min-h-[calc(100vh-4rem)] px-6 md:px-8 py-10">
      <div className="max-w-4xl mx-auto">
        <Link to="/store" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-4"><ArrowLeft size={14} weight="bold" /> Back to store</Link>
        <h1 className="font-display font-black text-4xl tracking-tighter mb-8">Your cart ({items.length})</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-3">
            {(pricing?.items || []).map((it) => (
              <div key={it.id} data-testid={`cart-item-${it.id}`} className="border border-border p-4 rounded-sm bg-card flex items-center justify-between">
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-primary mb-1">Tool · monthly</div>
                  <div className="font-display font-bold text-lg">{it.name}</div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="font-mono text-lg">₹{it.price_inr}</div>
                  <button onClick={() => remove(it.id)} data-testid={`remove-${it.id}`} className="p-2 hover:text-destructive"><Trash size={16} weight="bold" /></button>
                </div>
              </div>
            ))}

            <div className="mt-4 border border-border rounded-sm bg-card p-4">
              <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2 block">Promo code</label>
              {coupon && pricing?.coupon_code ? (
                <div className="flex items-center justify-between p-2 border border-primary/40 bg-primary/5 rounded-sm" data-testid="applied-coupon">
                  <div className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-primary" /><span className="font-mono text-sm">{coupon}</span><span className="text-xs text-primary">−{pricing.coupon_pct}%</span></div>
                  <button onClick={removeCoupon} data-testid="remove-coupon" className="p-1 hover:text-destructive"><X size={16} weight="bold" /></button>
                </div>
              ) : (
                <form onSubmit={submitCoupon} className="flex gap-2">
                  <Input placeholder="Enter code" value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())} data-testid="coupon-input" className="rounded-sm font-mono uppercase" />
                  <Button type="submit" variant="outline" data-testid="apply-coupon" className="rounded-sm">Apply</Button>
                </form>
              )}
              {couponInvalid && pricing?.coupon_error && (
                <div className="mt-2 text-xs text-destructive" data-testid="coupon-error">{pricing.coupon_error}</div>
              )}
            </div>
          </div>

          <div className="border border-border p-6 rounded-sm bg-card h-fit lg:sticky lg:top-20">
            <h3 className="font-display font-bold text-xl mb-4">Summary</h3>
            <div className="space-y-2 text-sm font-mono mb-4">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal ({pricing?.tool_count || 0} tools)</span><span>₹{pricing?.subtotal ?? 0}</span></div>
              {pricing?.bundle_pct > 0 && (
                <div className="flex justify-between text-primary" data-testid="bundle-discount-line">
                  <span className="flex items-center gap-1"><Percent size={12} weight="bold" /> Bundle discount ({pricing.bundle_pct}%)</span>
                  <span>−₹{pricing.bundle_discount}</span>
                </div>
              )}
              {pricing?.coupon_code && pricing.coupon_discount > 0 && (
                <div className="flex justify-between text-primary" data-testid="coupon-discount-line">
                  <span className="flex items-center gap-1"><Ticket size={12} weight="bold" /> {pricing.coupon_code} ({pricing.coupon_pct}%)</span>
                  <span>−₹{pricing.coupon_discount}</span>
                </div>
              )}
            </div>
            <div className="border-t border-border pt-4 mb-4">
              <div className="flex justify-between items-baseline">
                <span className="text-sm font-mono uppercase tracking-widest">Total</span>
                <span className="font-display font-black text-3xl" data-testid="cart-total">₹{pricing?.total ?? 0}</span>
              </div>
            </div>
            <Button onClick={checkout} disabled={busy || !user} data-testid="checkout-btn" className="w-full bg-primary text-black hover:bg-primary/90 font-bold rounded-sm">
              {busy ? "Processing..." : user ? "Checkout with Razorpay" : "Sign in to checkout"}
            </Button>
            {!user && <p className="text-xs text-center text-muted-foreground mt-3"><Link to="/login" className="text-primary hover:underline">Sign in</Link> or <Link to="/register" className="text-primary hover:underline">create an account</Link></p>}
            <p className="text-xs text-muted-foreground mt-3 text-center">30 days of access per tool</p>
          </div>
        </div>
      </div>
    </div>
  );
}
